/**
 * ============================================================================
 * SECURITY SETTINGS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Manage security settings and preferences
 */

import React, { useState, useEffect } from 'react';
import { useSecurity } from '../../hooks/useSecurity';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Switch from '../common/Switch';
import Spinner from '../common/Spinner';
import Tabs from '../common/Tabs';

/**
 * Security Settings Component
 * @returns {JSX.Element}
 */
const SecuritySettings = () => {
  const {
    securitySettings,
    loadSecuritySettings,
    updateSecuritySettings,
    enableLoginAlerts,
    disableLoginAlerts,
    loading,
  } = useSecurity();
  const { showSuccess, showError } = useToast();
  const { language } = useI18n();

  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    loginAlerts: {
      email: false,
      sms: false,
      push: false,
    },
    suspiciousActivityAlerts: true,
    twoFactorRequired: false,
    sessionTimeout: 30, // minutes
  });

  useEffect(() => {
    loadSecuritySettings();
  }, [loadSecuritySettings]);

  useEffect(() => {
    if (securitySettings) {
      setSettings((prev) => ({
        ...prev,
        ...securitySettings,
      }));
    }
  }, [securitySettings]);

  const handleUpdateSettings = async (updates) => {
    try {
      const newSettings = { ...settings, ...updates };
      await updateSecuritySettings(newSettings);
      setSettings(newSettings);
      showSuccess(
        language === 'fr' ? 'Paramètres mis à jour' : 'Settings updated'
      );
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de mise à jour' : 'Failed to update')
      );
    }
  };

  const handleToggleLoginAlerts = async (enabled) => {
    try {
      if (enabled) {
        await enableLoginAlerts(settings.loginAlerts);
      } else {
        await disableLoginAlerts();
      }
      await loadSecuritySettings();
      showSuccess(
        language === 'fr' ? 'Alertes mises à jour' : 'Alerts updated'
      );
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de mise à jour' : 'Failed to update')
      );
    }
  };

  const tabs = [
    { id: 'general', label: language === 'fr' ? 'Général' : 'General', icon: '⚙️' },
    { id: 'alerts', label: language === 'fr' ? 'Alertes' : 'Alerts', icon: '🔔' },
    { id: 'sessions', label: language === 'fr' ? 'Sessions' : 'Sessions', icon: '🔐' },
  ];

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-6">
        {/* Header */}
        <h3 className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
          <span className="text-2xl">🛡️</span>
          {language === 'fr' ? 'Paramètres de sécurité' : 'Security Settings'}
        </h3>

        {/* Tabs */}
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} variant="pills" />

        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="space-y-4">
            <Card variant="outlined" className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-black text-gray-900 dark:text-white mb-1">
                    {language === 'fr' ? 'Alertes d\'activité suspecte' : 'Suspicious Activity Alerts'}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {language === 'fr'
                      ? 'Recevez des alertes en cas d\'activité suspecte sur votre compte'
                      : 'Get alerts for suspicious activity on your account'}
                  </p>
                </div>
                <Switch
                  checked={settings.suspiciousActivityAlerts}
                  onChange={(checked) =>
                    handleUpdateSettings({ suspiciousActivityAlerts: checked })
                  }
                />
              </div>
            </Card>

            <Card variant="outlined" className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-black text-gray-900 dark:text-white mb-1">
                    {language === 'fr' ? '2FA requis' : 'Require 2FA'}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {language === 'fr'
                      ? 'Exiger l\'authentification à deux facteurs pour toutes les connexions'
                      : 'Require two-factor authentication for all logins'}
                  </p>
                </div>
                <Switch
                  checked={settings.twoFactorRequired}
                  onChange={(checked) =>
                    handleUpdateSettings({ twoFactorRequired: checked })
                  }
                />
              </div>
            </Card>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div className="space-y-4">
            <Card variant="outlined" className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-black text-gray-900 dark:text-white mb-1">
                    {language === 'fr' ? 'Alertes de connexion' : 'Login Alerts'}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {language === 'fr'
                      ? 'Recevez des notifications lors de nouvelles connexions'
                      : 'Get notified when someone logs into your account'}
                  </p>
                </div>
                <Switch
                  checked={
                    settings.loginAlerts?.email ||
                    settings.loginAlerts?.sms ||
                    settings.loginAlerts?.push
                  }
                  onChange={handleToggleLoginAlerts}
                />
              </div>
              {(settings.loginAlerts?.email ||
                settings.loginAlerts?.sms ||
                settings.loginAlerts?.push) && (
                <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {language === 'fr' ? 'Email' : 'Email'}
                    </span>
                    <Switch
                      checked={settings.loginAlerts?.email || false}
                      onChange={(checked) => {
                        const newAlerts = {
                          ...settings.loginAlerts,
                          email: checked,
                        };
                        handleUpdateSettings({ loginAlerts: newAlerts });
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {language === 'fr' ? 'SMS' : 'SMS'}
                    </span>
                    <Switch
                      checked={settings.loginAlerts?.sms || false}
                      onChange={(checked) => {
                        const newAlerts = {
                          ...settings.loginAlerts,
                          sms: checked,
                        };
                        handleUpdateSettings({ loginAlerts: newAlerts });
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {language === 'fr' ? 'Notifications push' : 'Push Notifications'}
                    </span>
                    <Switch
                      checked={settings.loginAlerts?.push || false}
                      onChange={(checked) => {
                        const newAlerts = {
                          ...settings.loginAlerts,
                          push: checked,
                        };
                        handleUpdateSettings({ loginAlerts: newAlerts });
                      }}
                    />
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-4">
            <Card variant="outlined" className="p-4">
              <div>
                <h4 className="font-black text-gray-900 dark:text-white mb-1">
                  {language === 'fr' ? 'Délai d\'expiration de session' : 'Session Timeout'}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  {language === 'fr'
                    ? 'Temps d\'inactivité avant déconnexion automatique'
                    : 'Inactivity time before automatic logout'}
                </p>
                <select
                  value={settings.sessionTimeout}
                  onChange={(e) =>
                    handleUpdateSettings({ sessionTimeout: parseInt(e.target.value) })
                  }
                  className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={15}>15 {language === 'fr' ? 'minutes' : 'minutes'}</option>
                  <option value={30}>30 {language === 'fr' ? 'minutes' : 'minutes'}</option>
                  <option value={60}>1 {language === 'fr' ? 'heure' : 'hour'}</option>
                  <option value={120}>2 {language === 'fr' ? 'heures' : 'hours'}</option>
                  <option value={240}>4 {language === 'fr' ? 'heures' : 'hours'}</option>
                </select>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SecuritySettings;
